import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

/**
 * Custom TranslateLoader that reads from Capacitor Filesystem first,
 * falls back to bundled HTTP assets if no synced file exists.
 * 
 * Flow:
 * 1. Try to read from filesystem (Directory.Data/i18n/{lang}.json)
 * 2. If found, return parsed JSON
 * 3. If not found, fallback to HTTP (./assets/i18n/{lang}.json)
 */
export class CapacitorFileTranslateLoader implements TranslateLoader {
  
  constructor(private http: HttpClient) {}

  /**
   * Get translations for a given language
   * Tries filesystem first, falls back to bundled assets
   */
  getTranslation(lang: string): Observable<any> {
    // First try to read from filesystem (synced translations)
    return from(this.readFromFilesystem(lang)).pipe(
      switchMap(fileContent => {
        if (typeof fileContent === 'string' && fileContent.trim().length > 0) {
          // Found synced file in filesystem
          console.log(`CapacitorFileLoader: Using synced translations for ${lang}`);
          return of(JSON.parse(fileContent));
        }
        // No synced file, fallback to bundled assets
        console.log(`CapacitorFileLoader: No synced file for ${lang}, using bundled assets`);
        return this.http.get(`./assets/i18n/${lang}.json`);
      }),
      catchError(error => {
        console.warn(`CapacitorFileLoader: Error reading ${lang} from filesystem, using bundled assets`, error);
        // Fallback to bundled assets on any error
        return this.http.get(`./assets/i18n/${lang}.json`);
      })
    );
  }

  /**
   * Read translation file from Capacitor Filesystem
   * @param lang Language code (e.g., 'en', 'cy')
   * @returns Promise with file content or null if not found
   */
  private async readFromFilesystem(lang: string): Promise<string | null> {
    try {
      const result = await Filesystem.readFile({
        path: `i18n/${lang}.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8
      });
      if (typeof result.data === 'string') {
        return result.data;
      }
      // If Blob (web), try to convert to string
      if (result.data instanceof Blob) {
        return await result.data.text();
      }
      return null;
    } catch {
      // File doesn't exist or can't be read
      return null;
    }
  }

  /**
   * Check if a synced translation file exists in filesystem
   * Useful for debugging or manual sync status
   */
  async hasSyncedFile(lang: string): Promise<boolean> {
    const content = await this.readFromFilesystem(lang);
    return !!(content && content.trim().length > 0);
  }
}